// modules/inicio/pages/Registro.js
// Registro público de cuentas. La cuenta se crea en estado "pendiente" y NO
// puede iniciar sesión hasta que un administrador la apruebe desde
// Administración de usuarios (ahí también define rol y estrategias finales).

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form, Button, Row, Col } from "react-bootstrap";
import Swal from "sweetalert2";
import AuthShell from "../components/AuthShell";
import CampoPassword from "../components/CampoPassword";
import { registroAPI } from "../api/authAPI";

// Debe coincidir con ESTRATEGIAS_DISPONIBLES de ModalUsuario.js
const ESTRATEGIAS = [
  { value: "rbc", label: "♿ Discapacidad (RBC)" },
  { value: "vejez", label: "🧓 Vejez" },
  { value: "migrante", label: "🧭 Población Migrante" },
  { value: "habitante_calle", label: "🏙️ Habitante de Calle" },
];

const FORM_VACIO = {
  nombre: "",
  identificacion: "",
  email: "",
  numero_contrato: "",
  password: "",
  confirmar: "",
  programas: [],
};

export default function Registro() {
  const navigate = useNavigate();
  const [form, setForm] = useState(FORM_VACIO);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  function alternarPrograma(valor) {
    setForm((f) => ({
      ...f,
      programas: f.programas.includes(valor)
        ? f.programas.filter((p) => p !== valor)
        : [...f.programas, valor],
    }));
  }

  async function manejarSubmit(e) {
    e.preventDefault();

    if (form.password.length < 6) {
      Swal.fire({ icon: "warning", title: "Contraseña muy corta", text: "Debe tener al menos 6 caracteres.", confirmButtonColor: "#1a3a5c" });
      return;
    }
    if (form.password !== form.confirmar) {
      Swal.fire({ icon: "warning", title: "Las contraseñas no coinciden", confirmButtonColor: "#1a3a5c" });
      return;
    }

    setEnviando(true);
    try {
      await registroAPI({
        nombre: form.nombre.trim(),
        identificacion: form.identificacion.trim(),
        email: form.email.trim().toLowerCase(),
        numero_contrato: form.numero_contrato.trim(),
        password: form.password,
        programas: form.programas,
      });
      setEnviado(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "No se pudo enviar la solicitud",
        text: error?.response
          ? error.response.data?.error || "Revisa los datos e inténtalo de nuevo."
          : "Para crear una cuenta necesitas conexión a internet.",
        confirmButtonColor: "#1a3a5c",
      });
    } finally {
      setEnviando(false);
    }
  }

  const panelInfo = (
    <ol className="auth-pasos">
      <li><b>1</b><span>Llena tus datos y elige las estrategias en las que trabajas.</span></li>
      <li><b>2</b><span>El administrador revisa tu solicitud y define tus permisos.</span></li>
      <li><b>3</b><span>Cuando te aprueben, ingresas con tu identificación y contraseña.</span></li>
    </ol>
  );

  if (enviado) {
    return (
      <AuthShell titulo="Solicitud enviada" texto="Ya casi: solo falta la aprobación del administrador." extra={panelInfo} estrecha>
        <div className="auth-exito">
          <div className="icono">⏳</div>
          <h2>¡Gracias, {form.nombre.split(" ")[0]}!</h2>
          <p className="auth-sub" style={{ marginBottom: 18 }}>
            Tu cuenta quedó <strong>pendiente de aprobación</strong>. Podrás iniciar sesión con tu número de
            identificación <strong>{form.identificacion}</strong> cuando el administrador la active.
          </p>
          <Button className="btn-ps auth-btn w-100" onClick={() => navigate("/login")}>
            Volver a iniciar sesión
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      kicker="Nueva cuenta"
      titulo="Solicita tu acceso"
      texto="Crea tu cuenta para registrar información de las estrategias de Promoción Social."
      extra={panelInfo}
    >
      <h2>Crear cuenta</h2>
      <div className="auth-sub">Todos los campos son obligatorios.</div>

      <Form onSubmit={manejarSubmit}>
        <Form.Group className="mb-3" controlId="reg-nombre">
          <Form.Label>Nombre completo</Form.Label>
          <Form.Control value={form.nombre} onChange={set("nombre")} autoComplete="name" placeholder="Nombres y apellidos" required autoFocus />
        </Form.Group>

        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group controlId="reg-identificacion">
              <Form.Label>Número de identificación</Form.Label>
              <Form.Control value={form.identificacion} onChange={set("identificacion")} inputMode="numeric" autoComplete="username" placeholder="Cédula" required />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group controlId="reg-contrato">
              <Form.Label>Número de contrato</Form.Label>
              <Form.Control value={form.numero_contrato} onChange={set("numero_contrato")} placeholder="Ej. 3711-2026" required />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3" controlId="reg-email">
          <Form.Label>Correo electrónico</Form.Label>
          <Form.Control type="email" value={form.email} onChange={set("email")} autoComplete="email" placeholder="nombre@correo.com" required />
        </Form.Group>

        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group controlId="reg-password">
              <Form.Label>Contraseña</Form.Label>
              <CampoPassword value={form.password} onChange={set("password")} autoComplete="new-password" placeholder="Mínimo 6 caracteres" minLength={6} required />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group controlId="reg-confirmar">
              <Form.Label>Confirmar contraseña</Form.Label>
              <CampoPassword
                value={form.confirmar}
                onChange={set("confirmar")}
                autoComplete="new-password"
                placeholder="Repítela"
                isInvalid={form.confirmar.length > 0 && form.confirmar !== form.password}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Estrategias en las que trabajas</Form.Label>
          <div className="auth-chips">
            {ESTRATEGIAS.map((e) => {
              const activo = form.programas.includes(e.value);
              return (
                <button
                  type="button"
                  key={e.value}
                  className={`auth-chip ${activo ? "activo" : ""}`}
                  aria-pressed={activo}
                  onClick={() => alternarPrograma(e.value)}
                >
                  {activo ? "✓ " : ""}{e.label}
                </button>
              );
            })}
          </div>
          <Form.Text className="text-muted" style={{ fontSize: "0.76rem" }}>
            Es una solicitud: el administrador confirma tus permisos al aprobar la cuenta.
          </Form.Text>
        </Form.Group>

        <Button type="submit" className="w-100 btn-ps auth-btn" disabled={enviando}>
          {enviando ? "Enviando solicitud..." : "Enviar solicitud"}
        </Button>
      </Form>

      <div className="text-center mt-3">
        <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>¿Ya tienes cuenta? </span>
        <Link to="/login" className="auth-link">Inicia sesión</Link>
      </div>
    </AuthShell>
  );
}
